import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, Users, Check, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useBusiness } from "@/context/BusinessContext";
import { useSettings } from "@/context/SettingsContext";

export default function BookingPage() {
  const { business } = useBusiness();
  const { settings } = useSettings();
  const venueName = business?.business_name || business?.name || "us";

  // Always start at the top of the form when the page is opened,
  // including when reached via a footer "Book a table" link from
  // another route.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState(2);
  const [tables, setTables] = useState([]);
  const [tableId, setTableId] = useState("");
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", special_requests: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/tables");
      setTables(data);
    })();
  }, []);

  useEffect(() => {
    if (!date) {
      setAvailability(null);
      return;
    }
    setLoadingAvail(true);
    setTime("");
    setTableId("");
    api
      .get("/availability", {
        params: { date: format(date, "yyyy-MM-dd"), guests },
      })
      .then(({ data }) => setAvailability(data))
      .catch(() => setAvailability({ open: false, slots: [] }))
      .finally(() => setLoadingAvail(false));
  }, [date, guests]);

  const currentSlot = useMemo(() => {
    if (!availability || !time) return null;
    return availability.slots.find((s) => s.time === time) || null;
  }, [availability, time]);

  async function submit(e) {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please pick a date and time");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time,
        guests,
        table_id: tableId || null,
      };
      const { data } = await api.post("/bookings", payload);
      setConfirmed(data);
      toast.success("Reservation submitted!");

      // ── Auto-print kitchen docket ─────────────────────────
      // When the business has `auto_print_dockets=true` AND the
      // booking carries a `dietary_card` snapshot, silently
      // iframe the docket URL so the in-store thermal printer
      // gets the slip without any kitchen-staff clicks.
      try {
        const autoPrint = settings?.auto_print_dockets;
        const hasCard   = !!data?.dietary_card;
        if (autoPrint && hasCard && data?.id) {
          const width = settings?.docket_printer_width || "80mm";
          const url = `${process.env.REACT_APP_BACKEND_URL}`
                          + `/api/admin/kitchen/booking/${data.id}/docket`
                          + `?width=${width}`;
          const ifr = document.createElement("iframe");
          ifr.src = url;
          ifr.style.cssText = "position:fixed;width:0;height:0;border:0;left:-9999px;";
          ifr.setAttribute("data-testid", "auto-print-frame");
          document.body.appendChild(ifr);
          // Clean up after print dialog has fired (the docket
          // HTML invokes window.print() on its own load handler)
          setTimeout(() => ifr.remove(), 15000);
        }
      } catch (_e) { /* non-fatal */ }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (confirmed) {
    return (
      <div
        data-testid="booking-success"
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 text-center"
      >
        <div className="h-16 w-16 rounded-full bg-[#5A7247]/10 text-[#5A7247] flex items-center justify-center mx-auto">
          <Check className="h-8 w-8" strokeWidth={1.5} />
        </div>
        <h1 className="font-serif-display text-5xl mt-6">
          Grazie, {confirmed.name.split(" ")[0]}!
        </h1>
        <p className="mt-4 text-[#5C625E] leading-relaxed">
          We've received your reservation for <strong>{confirmed.guests}</strong> on{" "}
          <strong>{confirmed.date}</strong> at <strong>{confirmed.time}</strong>. Reference:{" "}
          <span className="font-mono text-sm">{confirmed.id.slice(0, 8)}</span>
        </p>
      </div>
    );
  }

  // Compute table availability for the chosen slot
  const tableAvailability = new Map();
  if (currentSlot) {
    for (const t of currentSlot.available_tables) tableAvailability.set(t.id, t);
  }

  return (
    <div data-testid="booking-page" id="book" className="bg-[#FDFBF7] scroll-mt-24">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-12">
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">Reserve</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3">
          A table at {venueName}.
        </h1>
        <p className="mt-4 text-[#5C625E] max-w-2xl">
          Choose a date, party size, time, and table — availability updates live.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-7 bg-white rounded-2xl border border-[#E8E5DF] p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="booking-date-trigger"
                      className="w-full mt-2 justify-start text-left h-12 rounded-xl border-[#E8E5DF] font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" strokeWidth={1.5} />
                      {date ? format(date, "EEE, MMM d") : "Select a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Guests</Label>
                <Select
                  value={String(guests)}
                  onValueChange={(v) => setGuests(parseInt(v))}
                >
                  <SelectTrigger
                    data-testid="booking-guests-select"
                    className="mt-2 h-12 rounded-xl"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((g) => (
                      <SelectItem key={g} value={String(g)}>
                        {g} {g === 1 ? "guest" : "guests"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Time</Label>
              {availability?.holiday && (
                <div
                  data-testid="booking-holiday-banner"
                  className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900"
                >
                  <p className="font-bold uppercase tracking-wider text-[10px]">
                    {availability.holiday.name}
                  </p>
                  {availability.holiday.menu_note && (
                    <p className="italic mt-0.5">{availability.holiday.menu_note}</p>
                  )}
                  {availability.holiday.is_closed && (
                    <p className="mt-0.5">We&apos;re closed on this date. Please pick another day.</p>
                  )}
                </div>
              )}
              {!date && (
                <p className="text-sm text-[#5C625E] mt-2">Pick a date to see available times.</p>
              )}
              {date && loadingAvail && (
                <p data-testid="booking-loading-avail" className="text-sm text-[#5C625E] mt-2">
                  Checking availability…
                </p>
              )}
              {date && !loadingAvail && availability && !availability.open && (
                <p data-testid="booking-closed" className="text-sm text-[#C05746] mt-2">
                  We're closed on this day. Try another date.
                </p>
              )}
              {date && !loadingAvail && availability?.open && (
                <>
                  {/* Sittings capacity bar */}
                  {Array.isArray(availability.sittings) && availability.sittings.length > 0 && (
                    <div
                      data-testid="booking-sittings-bar"
                      className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2"
                    >
                      {availability.sittings.map((s) => {
                        const pct = s.max_patrons
                          ? Math.min(100, Math.round((s.booked_patrons / s.max_patrons) * 100))
                          : 0;
                        const tone = s.is_full
                          ? "border-red-300 bg-red-50 text-red-900"
                          : pct > 70
                          ? "border-amber-300 bg-amber-50 text-amber-900"
                          : "border-[#E8E5DF] bg-white text-[#121614]";
                        return (
                          <div
                            key={s.name}
                            data-testid={`booking-sitting-${(s.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                            className={`rounded-xl border ${tone} p-2.5`}
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-bold uppercase tracking-wider">
                                {s.name}
                              </p>
                              <p className="text-[10px] text-[#5C625E]">
                                {s.start}–{s.end}
                              </p>
                            </div>
                            <div className="mt-1 h-1.5 w-full bg-black/5 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${
                                  s.is_full ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-[#5A7247]"
                                }`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <p className="text-xs mt-1">
                              {s.is_full
                                ? "Sitting full"
                                : `${s.booked_patrons} / ${s.max_patrons || "—"} seated`}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {availability.next_sitting_suggestion && (
                    <div
                      data-testid="booking-next-sitting-suggestion"
                      className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                    >
                      <strong>Heads up:</strong> the earlier sitting is full —
                      try{" "}
                      <button
                        type="button"
                        data-testid="booking-next-sitting-jump"
                        onClick={() => {
                          setTime(availability.next_sitting_suggestion.time);
                          setTableId("");
                        }}
                        className="font-bold underline"
                      >
                        {availability.next_sitting_suggestion.time}
                      </button>{" "}
                      ({availability.next_sitting_suggestion.sitting}).
                    </div>
                  )}

                  <Select value={time} onValueChange={(v) => { setTime(v); setTableId(""); }}>
                    <SelectTrigger
                      data-testid="booking-time-select"
                      className="mt-3 h-12 rounded-xl"
                    >
                      <SelectValue placeholder="Choose a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {availability.slots.map((s) => {
                        const disabled = s.blocked || s.available_tables.length === 0 || s.reason === "sitting_full";
                        const reasonLabel =
                          s.reason === "blocked"       ? "private event"   :
                          s.reason === "fully_booked"  ? "fully booked"    :
                          s.reason === "sitting_full"  ? `${s.sitting || "sitting"} full` :
                          s.reason === "party_too_large"
                            ? `biggest free table seats ${s.max_table_seats || 0} (need ${guests})` :
                          "unavailable";
                        const sittingChip = s.sitting ? ` · ${s.sitting}` : "";
                        return (
                          <SelectItem
                            key={s.time}
                            value={s.time}
                            disabled={disabled}
                            data-testid={`booking-time-${s.time}`}
                          >
                            {s.time}
                            {sittingChip}{" "}
                            {disabled
                              ? `· ${reasonLabel}`
                              : `· ${s.available_seats} seat${
                                  s.available_seats === 1 ? "" : "s"
                                } free`}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>

            {time && tables.length > 0 && (
              <div>
                <Label>Choose a table</Label>
                <div
                  data-testid="booking-floor-map"
                  className="mt-3 relative bg-[#F3F0EA] rounded-2xl border border-[#E8E5DF] overflow-hidden"
                  style={{ height: 360 }}
                >
                  {tables.map((t) => {
                    const free = tableAvailability.has(t.id);
                    const selected = tableId === t.id;
                    return (
                      <button
                        type="button"
                        key={t.id}
                        data-testid={`booking-table-${t.id}`}
                        disabled={!free}
                        onClick={() => setTableId(t.id)}
                        className={`absolute flex flex-col items-center justify-center transition-all border-2 ${
                          t.shape === "circle" ? "rounded-full" : "rounded-lg"
                        } ${
                          selected
                            ? "bg-[#C05746] text-white border-[#C05746] shadow-lg z-10"
                            : free
                            ? "bg-white text-[#121614] border-[#5A7247]/40 hover:border-[#5A7247] cursor-pointer"
                            : "bg-[#E8E5DF] text-[#9E9E9E] border-[#E8E5DF] cursor-not-allowed"
                        }`}
                        style={{ left: t.x, top: t.y, width: t.width, height: t.height }}
                      >
                        <span className="text-sm font-medium">{t.name}</span>
                        <span className="text-[10px] uppercase tracking-wider flex items-center gap-1">
                          <Users className="h-3 w-3" strokeWidth={1.5} />
                          {t.seats}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#5C625E] mt-2 flex items-center gap-1">
                  <MapPin className="h-3 w-3" strokeWidth={1.5} />
                  Tap a highlighted table, or leave unselected to let us assign one.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="b-name">Full name</Label>
                <Input
                  id="b-name"
                  data-testid="booking-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 rounded-xl h-11"
                />
              </div>
              <div>
                <Label htmlFor="b-phone">Phone</Label>
                <Input
                  id="b-phone"
                  data-testid="booking-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 rounded-xl h-11"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="b-email">Email</Label>
              <Input
                id="b-email"
                data-testid="booking-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 rounded-xl h-11"
              />
            </div>
            <div>
              <Label htmlFor="b-notes">Special requests</Label>
              <Textarea
                id="b-notes"
                data-testid="booking-notes"
                value={form.special_requests}
                onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                placeholder="Anniversary, dietary needs, seating preferences…"
                className="mt-2 rounded-xl"
              />
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-[#F3F0EA] rounded-2xl p-6 md:p-8 sticky top-28">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
                Your reservation
              </p>
              <h3 className="font-serif-display text-3xl mt-2">Almost there.</h3>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex justify-between border-b border-[#E8E5DF] pb-2">
                  <span className="text-[#5C625E]">Date</span>
                  <span data-testid="booking-summary-date">
                    {date ? format(date, "PPP") : "—"}
                  </span>
                </li>
                <li className="flex justify-between border-b border-[#E8E5DF] pb-2">
                  <span className="text-[#5C625E]">Time</span>
                  <span data-testid="booking-summary-time">{time || "—"}</span>
                </li>
                <li className="flex justify-between border-b border-[#E8E5DF] pb-2">
                  <span className="text-[#5C625E]">Guests</span>
                  <span data-testid="booking-summary-guests" className="inline-flex items-center gap-1">
                    <Users className="h-4 w-4" strokeWidth={1.5} /> {guests}
                  </span>
                </li>
                <li className="flex justify-between border-b border-[#E8E5DF] pb-2">
                  <span className="text-[#5C625E]">Table</span>
                  <span data-testid="booking-summary-table">
                    {tableId ? tables.find((t) => t.id === tableId)?.name : "Auto-assign"}
                  </span>
                </li>
              </ul>
              <Button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting}
                className="w-full mt-8 bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-12"
              >
                {submitting ? "Reserving…" : "Confirm reservation"}
              </Button>
              <p className="mt-4 text-xs text-[#5C625E]">
                No payment required. We'll hold your table for 15 minutes after your time.
              </p>
            </div>
          </aside>
        </form>
      </section>
    </div>
  );
}
